import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Plus, SlidersHorizontal } from 'lucide-react';
import orgApi from '../../api/organizations';
import { useAuth } from '../../auth/AuthProvider';
import useStore from '../../store/useStore';
import TemplateRolePrompt from '../TemplateRolePrompt';

const INDUSTRIES = ['Technology', 'Manufacturing', 'Finance', 'Healthcare', 'Retail', 'Other'];

const normalize = (data) => (Array.isArray(data) ? data : data?.tenants || []);

// Sidebar organisation switcher. Replaces the old Workspaces tab on
// /org/members: it's about which org you're in, not this org's settings.
const OrgSwitcher = () => {
  const { user, switchOrg } = useAuth();
  const navigate = useNavigate();
  const open = useStore(s => s.orgSwitcherOpen);
  const setOpen = useStore(s => s.setOrgSwitcherOpen);
  const [orgs, setOrgs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [industry, setIndustry] = useState('Technology');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newOrg, setNewOrg] = useState(null);
  const buttonRef = useRef(null);
  const popRef = useRef(null);

  const activeId = user?.tenant_id;
  const active = orgs.find(o => o.id === activeId);

  useEffect(() => {
    orgApi.listWorkspaces().then(d => setOrgs(normalize(d))).catch(() => setOrgs([]));
  }, [activeId]);

  useEffect(() => {
    if (!open) { setCreating(false); setError(null); return undefined; }
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await orgApi.createOrg(newName.trim(), industry);
      const id = res?.tenant_id || res?.id;
      if (!id) { setOpen(false); window.location.reload(); return; }
      const rolesRes = await orgApi.getRoles(id).catch(() => null);
      const admin = (rolesRes?.roles || []).find(r => r.is_system);
      setNewOrg({ id, adminRoleId: admin?.id || null });
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to create organisation.');
    } finally {
      setBusy(false);
    }
  };

  const finishCreate = async () => {
    const id = newOrg?.id;
    setNewOrg(null);
    if (id) await switchOrg(id);
  };

  const label = active?.name || (activeId ? `Org ${activeId.substring(0, 8)}` : 'No organisation');

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="w-full flex items-center justify-between h-[52px] px-3 rounded-lg border text-left transition-colors"
        style={{ background: 'var(--surf-bg)', borderColor: open ? 'var(--accent)' : 'var(--surf-line)' }}
      >
        <span className="flex flex-col gap-0.5 min-w-0">
          <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>ORGANISATION</span>
          <span className="text-sm font-medium truncate" style={{ color: 'var(--ink-text)' }}>{label}</span>
        </span>
        <ChevronsUpDown className="w-4 h-4 shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
      </button>

      {/* Portalled: the sidebar is transformed + overflow-hidden, which would clip a fixed child. */}
      {open && createPortal(
        <div
          ref={popRef}
          role="dialog"
          aria-label="Switch organisation"
          className="fixed left-3 top-[136px] z-40 w-[320px] max-w-[calc(100vw-24px)] box-border p-2 rounded-xl border flex flex-col gap-0.5"
          style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 16px 40px rgba(0,0,0,0.35)', color: 'var(--ink-text)' }}
        >
          <span className="font-mono text-[10.5px] tracking-[0.12em] px-2.5 pt-2.5 pb-1.5" style={{ color: 'var(--ink-faint)' }}>YOUR ORGANISATIONS</span>
          <div className="max-h-[320px] overflow-y-auto flex flex-col gap-0.5">
            {orgs.map(o => {
              const isActive = o.id === activeId;
              const meta = [o.role, o.member_count != null ? `${o.member_count} member${o.member_count === 1 ? '' : 's'}` : null]
                .filter(Boolean).join(' · ');
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { if (!isActive) switchOrg(o.id); else setOpen(false); }}
                  aria-current={isActive ? 'true' : undefined}
                  className="h-[52px] shrink-0 flex items-center gap-3 px-2.5 rounded-lg text-left"
                  style={{ background: isActive ? 'var(--surf-raised)' : 'transparent' }}
                >
                  <span className="w-8 h-8 rounded-lg border flex items-center justify-center font-heading text-sm font-semibold shrink-0" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line)' }}>
                    {(o.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-grow min-w-0 flex flex-col gap-0.5">
                    <span className="text-sm font-medium truncate">{o.name}</span>
                    {meta && <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{meta}</span>}
                  </span>
                  {isActive && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--accent)' }} strokeWidth={2.2} />}
                </button>
              );
            })}
            {orgs.length === 0 && (
              <span className="px-2.5 py-2 text-[13px]" style={{ color: 'var(--ink-faint)' }}>You’re not in any organisation yet.</span>
            )}
          </div>
          <span className="h-px my-1.5 mx-1" style={{ background: 'var(--surf-line)' }} />

          {creating ? (
            <form onSubmit={create} className="flex flex-col gap-2 px-2.5 py-2">
              <label htmlFor="new-org-name" className="text-[12px] font-medium" style={{ color: 'var(--ink-muted)' }}>Organisation name</label>
              <input
                id="new-org-name"
                autoFocus
                required
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Client Alpha Sdn Bhd"
                className="h-10 px-3 rounded-lg border text-sm outline-none"
                style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
              />
              <label htmlFor="new-org-industry" className="text-[12px] font-medium" style={{ color: 'var(--ink-muted)' }}>Industry sector</label>
              <select
                id="new-org-industry"
                value={industry}
                onChange={e => setIndustry(e.target.value)}
                className="h-10 px-2.5 rounded-lg border text-sm"
                style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
              >
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
              {error && <span className="text-xs" style={{ color: 'var(--sev-crit-fg)' }}>{error}</span>}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setCreating(false)} className="h-10 px-3 rounded-lg text-sm" style={{ color: 'var(--ink-muted)' }}>Cancel</button>
                <button type="submit" disabled={busy} className="h-10 px-3.5 rounded-lg text-sm font-semibold disabled:opacity-50" style={{ background: 'var(--accent)', color: '#0A0A0A' }}>
                  {busy ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          ) : (
            <button type="button" onClick={() => setCreating(true)} className="h-11 flex items-center gap-3 px-2.5 rounded-lg text-sm text-left">
              <Plus className="w-4 h-4" strokeWidth={1.8} />Create organisation
            </button>
          )}
          {activeId && !creating && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/org/members?drawer=org'); }}
              className="h-11 flex items-center gap-3 px-2.5 rounded-lg text-sm text-left"
              style={{ color: 'var(--ink-muted)' }}
            >
              <SlidersHorizontal className="w-4 h-4" strokeWidth={1.8} />Organisation details
            </button>
          )}
          <p className="mx-2.5 mt-1 mb-2 text-[11.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>
            Each organisation is isolated — its own assets, events, members and audit log.
          </p>
        </div>,
        document.body,
      )}

      {newOrg && createPortal(
        <TemplateRolePrompt
          tenantId={newOrg.id}
          adminRoleId={newOrg.adminRoleId}
          onComplete={finishCreate}
          onSkip={finishCreate}
        />,
        document.body,
      )}
    </>
  );
};

export default OrgSwitcher;
